import { Icon, Loader, Popover } from '../../components';
import { useTranslation } from '../../providers/UIProvider';
import { ActivityCard } from './ActivityCard';
import { summarizeNotifications } from './summarize';
import type { NotificationView } from './types';

interface StackedActivitiesProps {
  activities: NotificationView[];
  onOpen: (activity: NotificationView) => void;
}

/**
 * Collapsed = an aggregated count summary on top of an offset "deck" (lock-screen style);
 * clicking opens a Popover listing every notification.
 */
export function StackedActivities({ activities, onOpen }: StackedActivitiesProps) {
  const t = useTranslation('notifications');
  const extra = activities.length - 1;
  const segments = summarizeNotifications(activities);
  const summary = segments.map(segment => t(segment.key, { count: segment.count }));
  const anyInProgress = activities.some(activity => activity.progress != null);

  const trigger = (
    <button
      type='button'
      aria-label={t('expand', { count: activities.length })}
      className='group relative block w-full text-left'
    >
      {/* Peek cards stacked behind the summary — they rise away from the screen corner. */}
      {extra >= 2 && (
        <span
          aria-hidden
          className='absolute -top-2 left-4 right-4 h-full rounded-full bg-background-input opacity-40 shadow-lg'
        />
      )}
      {extra >= 1 && (
        <span
          aria-hidden
          className='absolute -top-1 left-2 right-2 h-full rounded-full bg-background-input opacity-60 shadow-lg'
        />
      )}
      <span className='relative flex items-center gap-sm rounded-full bg-background-input px-md py-md shadow-lg'>
        {anyInProgress && (
          <span className='flex shrink-0 items-center'>
            <Loader size='sm' />
          </span>
        )}
        <span className='min-w-0 flex-1 truncate text-sm font-medium text-foreground-base'>
          {summary.join(' · ')}
        </span>
        {/* Points up to expand; Headless UI sets data-open on the trigger, flipping it to close. */}
        <Icon
          name='chevron-down'
          size='sm'
          className='shrink-0 rotate-180 text-foreground-muted transition-transform duration-200 group-data-[open]:rotate-0'
        />
      </span>
    </button>
  );

  return (
    // Anchored above the dock. `--anchor-gap` offsets the panel's built-in top margin;
    // `--anchor-padding` is the viewport inset Headless UI keeps when it sizes/shifts the panel,
    // which is the only lever here — it writes max-width inline, beating any class.
    <Popover
      triggerButton={trigger}
      align='top start'
      size='xl'
      className='p-sm [--anchor-gap:1.5rem] [--anchor-padding:1rem]'
    >
      {({ close }) => (
        <ul className='flex max-h-80 flex-col gap-2xs'>
          {activities.map(activity => (
            <li key={activity.id}>
              <ActivityCard
                activity={activity}
                seedKey={activity.id}
                variant='row'
                onOpen={next => {
                  onOpen(next);
                  close();
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </Popover>
  );
}
